import {usePlantConfig} from '@domain/shifthandover'
import {StoppageEvent} from '@hconnect/common/stoppages/types/stoppage.types'
import {StoppageDto as Stoppage} from '@hconnect/common/types'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {SplitStoppageDialog} from '@widgets/common-running-times'
import {StoppageCreate, StoppageDelete} from '@widgets/shift-event-details'
import {FC, useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'

import {StoppageSideCard} from './StoppageSideCard'

type StoppageViewProps = {
  stoppageToPreview: StoppageEvent | undefined
  setStoppageToPreview: (stoppage: StoppageEvent | undefined) => void
  stoppageIdToPreview: string | undefined
  setStoppageIdToPreview: (id: string | undefined) => void
  setShowCreateForm: (show: boolean) => void
  plantId: string
  showCreateForm: boolean
  stoppageData: Partial<StoppageEvent> | undefined
  handleStoppageSplit: (stoppage: Partial<Stoppage>) => void
  onCreate?: (stoppageId: string) => void
  onEdit?: (stoppageId: string) => void
  onDelete?: (stoppageId: string) => void
}

export const StoppageView: FC<StoppageViewProps> = ({
  plantId,
  showCreateForm,
  stoppageData,
  stoppageToPreview,
  setStoppageToPreview,
  setShowCreateForm,
  stoppageIdToPreview,
  setStoppageIdToPreview,
  handleStoppageSplit,
  onCreate,
  onEdit,
  onDelete
}) => {
  const {
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const {data: plantConfig} = usePlantConfig({plantId, language})
  const timezone = useUpmPlantTimezone(plantId)

  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [isSplitMode, _setIsSplitMode] = useState<boolean>(false)
  const [showSplitStoppageDialog, setShowSplitStoppageDialog] = useState<boolean>(false)
  const [showDeleteDialog, setDeleteDialog] = useState<boolean>(false)

  const onEditSuccess = useCallback(
    (stoppage: StoppageEvent) => {
      setIsEditMode(false)
      setStoppageToPreview(stoppage)
      onEdit?.(stoppage.id)
    },
    [setStoppageToPreview, onEdit]
  )
  const handleClose = useCallback(() => {
    setShowCreateForm(false)
    _setIsSplitMode(false)
  }, [setShowCreateForm])
  const onSubmit = useCallback(
    ({id}: Stoppage) => {
      handleClose()
      onCreate?.(id)
    },
    [handleClose, onCreate]
  )
  const handlePreviewClose = () => {
    setStoppageIdToPreview(undefined)
  }
  const setSplitMode = (stoppage: StoppageEvent) => {
    _setIsSplitMode(true)
    setShowSplitStoppageDialog(true)
    setStoppageToPreview(stoppage)
  }
  const handleDelete = (stoppage: StoppageEvent) => {
    setDeleteDialog(true)
    setStoppageToPreview(stoppage)
    onDelete?.(stoppage.id)
  }

  const onDeleteSuccess = useCallback(() => {
    setStoppageToPreview(undefined)
    setStoppageIdToPreview(undefined)
    setDeleteDialog(false)
  }, [setStoppageToPreview, setStoppageIdToPreview])

  return (
    <>
      {stoppageIdToPreview ? (
        <StoppageSideCard
          isEditMode={isEditMode}
          onEdit={setIsEditMode}
          onSplit={setSplitMode}
          onDelete={handleDelete}
          stoppageId={stoppageIdToPreview}
          onEditSuccess={onEditSuccess}
          plantId={plantId}
          onClose={handlePreviewClose}
          timezone={timezone}
        />
      ) : showCreateForm ? (
        <StoppageCreate
          plantId={plantId}
          data-test-id="stoppage-analysis-graph-stoppage-create"
          doClose={handleClose}
          initialData={stoppageData}
          onSuccessSubmit={onSubmit}
          skipOverlapValidation={isSplitMode}
        />
      ) : (
        <></>
      )}

      {showSplitStoppageDialog && stoppageToPreview && (
        <SplitStoppageDialog
          timezone={timezone}
          open={showSplitStoppageDialog}
          onClose={(submit) => {
            setShowSplitStoppageDialog(false)
            if (!submit) _setIsSplitMode(false)
          }}
          stoppage={stoppageToPreview}
          onSplitSubmit={handleStoppageSplit}
          pxTrendAvailable={plantConfig?.pxTrendAvailable}
        />
      )}

      {!!(showDeleteDialog && stoppageToPreview) && (
        <StoppageDelete
          plantId={plantId}
          onSuccess={onDeleteSuccess}
          setDialogOpen={setDeleteDialog}
          stoppage={stoppageToPreview}
          showDialog={true}
        />
      )}
    </>
  )
}
